import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
} from "@suraasa/placebo-ui"
import { LegacyRef, useState } from "react"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import { FaceDetectionView } from "./LeftPanel/_components/FaceDetectionView"

interface FaceCaptureProps {
  videoRef: LegacyRef<HTMLVideoElement>
  isCapturing: boolean
  isCameraLoading: boolean
  capturedImage: string | null
  onCapture: () => void
  onRetake: () => void
  onSubmit: () => void
}

const FaceCapture = ({
  videoRef,
  isCapturing,
  isCameraLoading,
  capturedImage,
  onCapture,
  onRetake,
  onSubmit,
}: FaceCaptureProps) => {
  const [showGuidelines, setShowGuidelines] = useState(false)

  return (
    <div className="flex h-full flex-col">
      <Dialog open={showGuidelines} onOpenChange={setShowGuidelines}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Capture Guidelines</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <FaceDetectionView />
          </DialogBody>
        </DialogContent>
      </Dialog>
      <div className="mb-3 text-black-600">
        <Typography variant="title2">Capture Face</Typography>
      </div>

      <div className="mb-4 text-onSurface-700">
        <Typography variant="body" className="italic">
          Please align yourself to the center of the screen and press
          &apos;Capture Your Face&apos; button.
        </Typography>
        <button
          onClick={() => {
            trackingService.trackEvent(
              TrackingEvents.cpatProcInitGuidelinesViewed,
              undefined,
              { services: ["PostHog"] }
            )
            setShowGuidelines(true)
          }}
          className="mt-1 text-sm font-medium text-primary-600 underline lg:hidden"
        >
          View Guidelines
        </button>
      </div>

      <div className="relative mb-6 aspect-video w-full overflow-hidden rounded-xl bg-black">
        {capturedImage ? (
          <img
            src={capturedImage}
            alt="Captured"
            className="size-full object-cover"
          />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="size-full -scale-x-100 object-cover"
          />
        )}

        {/* Camera initializing overlay */}
        {isCameraLoading && !capturedImage && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
            <div className="size-5 animate-spin rounded-full border-4 border-white/20 border-t-white" />
            <Typography variant="smallBody" className="text-white/80">
              Initializing camera…
            </Typography>
          </div>
        )}
      </div>

      {capturedImage ? (
        <div className="flex flex-col gap-2 md:flex-row">
          <Button
            variant="outlined"
            color="black"
            className="flex-1"
            onClick={() => {
              trackingService.trackEvent(
                TrackingEvents.cpatProcInitFaceRetakenClicked,
                undefined,
                { services: ["PostHog"] }
              )
              onRetake()
            }}
          >
            Retake
          </Button>
          <Button
            variant="filled"
            color="black"
            className="flex-1"
            onClick={onSubmit}
            loading={isCapturing}
            disabled={isCapturing}
          >
            Submit
          </Button>
        </div>
      ) : (
        <Button
          variant="filled"
          color="black"
          className="w-full"
          onClick={() => {
            trackingService.trackEvent(
              TrackingEvents.cpatProcInitFaceCaptureClicked,
              undefined,
              { services: ["PostHog"] }
            )
            onCapture()
          }}
          disabled={isCapturing || isCameraLoading}
        >
          Capture Your Face
        </Button>
      )}
    </div>
  )
}

export default FaceCapture
